// src/components/DroppedSavesNotice.jsx
// 2026-09-27 — окно «часть правок не сохранилась» (design v2 §2 «Окно (EN)»).
//
// Показывает ТОЛЬКО notice:'confirmed' — сервер (save-dropped.cjs) уже сверил снимок с
// диском (computeDroppedSaveVerdict) и подтвердил настоящую потерю; 'new' (ждёт отчёта)
// и 'seen' (уже закрыта) не показываются. Единственная кнопка — OK: Retry/Download
// НАМЕРЕННО нет (design v2 §1 «v1→v2», Sonnet R1/Grok) — повтор либо хоронит строку,
// которую админ уже вернул вручную, либо задваивает simple-таблицу при полной
// перезаписи; временные сбои теперь и не попадают на склад (см. W3 в api-client.js).
//
// Разметка — своя, не общий Modal.jsx (тот же довод, что у SessionExpiredDialog.jsx:
// это не сообщение, которое можно смахнуть мимо-кликом или крестиком).

import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/theme-context.js';
import { LS_DROPPED_SAVES_KEY } from '../lib/api-client.js';

const HUMAN_TABLE_NAMES = {
  'payments': 'Affiliate Payments',
  'customer-payments': 'Customer Payments',
  'crg-deals': 'CRG Deals',
  'daily-calcs-data': 'Daily Calcs',
};

// Signature, закрытая OK, не всплывает заново на протяжении суток (design v2 §2) — тот
// же принцип, что дедуп личики на сервере (save-dropped.cjs), только на клиенте и по
// одной кнопке OK, а не по времени между отчётами.
const ACK_DEDUP_MS = 24 * 60 * 60 * 1000;

function tableLabel(endpoint) {
  return HUMAN_TABLE_NAMES[endpoint] || endpoint;
}

function reasonLine(httpStatus) {
  if (httpStatus === 400 || httpStatus === 422) return 'The server rejected the save';
  if (httpStatus === 413) return 'The save was too large for the server';
  if (typeof httpStatus === 'number') return `The server refused the save (error ${httpStatus})`;
  return 'The save could not be delivered';
}

function readStore() {
  try {
    const raw = localStorage.getItem(LS_DROPPED_SAVES_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function writeStore(list) {
  try { localStorage.setItem(LS_DROPPED_SAVES_KEY, JSON.stringify(list)); } catch { /* см. persistDroppedSave — не критично */ }
}

function emitChanged() {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('blitz:dropped-saves-changed'));
    }
  } catch {}
}

/**
 * Разбирает склад: 'confirmed'-записи, чья (endpoint, signature) уже закрывалась OK
 * за последние 24ч, молча переводятся в 'seen' (без показа) — остальные 'confirmed'
 * идут в витрину. Возвращает { list, visible, changed } — list пишется обратно в
 * localStorage только если changed (не дёргаем storage на каждый пустой прогон).
 */
function partitionStore(list, nowTs) {
  const recentlyAcked = new Set();
  for (const e of list) {
    if (e && e.notice === 'seen' && e.verdict && e.verdict.signature && e.ackAt) {
      const ackedAt = Date.parse(e.ackAt);
      if (!Number.isNaN(ackedAt) && nowTs - ackedAt < ACK_DEDUP_MS) {
        recentlyAcked.add(`${e.endpoint}|${e.verdict.signature}`);
      }
    }
  }

  let changed = false;
  const next = list.map(e => {
    if (!e || e.notice !== 'confirmed') return e;
    const sig = e.verdict && e.verdict.signature;
    if (sig && recentlyAcked.has(`${e.endpoint}|${sig}`)) {
      changed = true;
      return { ...e, notice: 'seen', ackAt: new Date(nowTs).toISOString() };
    }
    return e;
  });
  const visible = next.filter(e => e && e.notice === 'confirmed');
  return { list: next, visible, changed };
}

export default function DroppedSavesNotice() {
  const { dark } = useTheme();
  const [visible, setVisible] = useState([]);

  const refresh = useCallback(() => {
    const { list, visible: v, changed } = partitionStore(readStore(), Date.now());
    if (changed) writeStore(list);
    setVisible(v);
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('blitz:dropped-saves-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('blitz:dropped-saves-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  const handleOk = useCallback(() => {
    const ackAt = new Date().toISOString();
    const ids = new Set(visible.map(e => e.id));
    const list = readStore().map(e => (e && ids.has(e.id) ? { ...e, notice: 'seen', ackAt } : e));
    writeStore(list);
    setVisible([]);
    emitChanged();
  }, [visible]);

  if (visible.length === 0) return null;

  const c = dark
    ? { card: 'rgba(18,24,41,0.97)', rim: 'rgba(99,179,237,0.22)', title: '#F0F6FF', body: '#9BB3CC' }
    : { card: '#FFFFFF', rim: '#E2E8F0', title: '#0F172A', body: '#475569' };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(5,7,15,0.85)',
        backdropFilter: 'blur(10px) saturate(180%)', WebkitBackdropFilter: 'blur(10px) saturate(180%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000000, padding: 12,
      }}
    >
      <div
        role="dialog" aria-modal="true" aria-label="Some changes were not saved"
        style={{
          background: c.card, borderRadius: 20, border: `1px solid ${c.rim}`, padding: 28,
          width: 460, maxWidth: '95vw',
          boxShadow: '0 25px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.05)',
        }}
      >
        <h2 style={{ margin: '0 0 16px', color: c.title, fontSize: 19, fontWeight: 800, letterSpacing: '-0.3px' }}>
          Some changes were not saved
        </h2>
        <div style={{ marginBottom: 18 }}>
          {visible.map(e => {
            const rows = (e.verdict && (e.verdict.lostRows + e.verdict.lostDeletes)) || 0;
            return (
              <p key={e.id} style={{ margin: '0 0 10px', color: c.body, fontSize: 14, lineHeight: 1.5 }}>
                <strong style={{ color: c.title }}>{tableLabel(e.endpoint)}</strong>
                {' — '}{rows} row{rows === 1 ? '' : 's'}. {reasonLine(e.httpStatus)}.
              </p>
            );
          })}
        </div>
        <p style={{ margin: '0 0 22px', color: c.body, fontSize: 13.5, lineHeight: 1.55 }}>
          The admin has been notified and will check. <strong style={{ color: c.title }}>Please don&apos;t re-enter these changes for now</strong> — that could create duplicates.
        </p>
        <button
          type="button"
          autoFocus
          onClick={handleOk}
          style={{
            width: '100%', padding: '13px 20px', borderRadius: 12, border: 'none',
            background: '#2563EB', color: '#FFFFFF', fontSize: 15, fontWeight: 700,
            cursor: 'pointer', transition: 'background 0.15s',
          }}
          onMouseEnter={ev => { ev.currentTarget.style.background = '#1D4ED8'; }}
          onMouseLeave={ev => { ev.currentTarget.style.background = '#2563EB'; }}
        >
          OK
        </button>
      </div>
    </div>
  );
}
